/**
 * Custom Contact Form - Topic-Based Conditional Form
 * Handles topic selection, field visibility, validation, and file uploads
 */

(function () {
  'use strict';

  var ccfDebug = window.location.search.includes('ccf_debug=1');

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    // console.log('[CCF] DOM is loading, waiting for DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // console.log('[CCF] DOM is ready, initializing immediately');
    init();
  }

  function init() {
    // console.log('[CCF] Initializing...');
    // Try multiple times to find containers (in case DOM isn't ready)
    let retryCount = 0;
    const maxRetries = 10;
    
    function tryInit() {
      const containers = document.querySelectorAll('.ccf-section');
      const altContainers = document.querySelectorAll('[id^="ccf-section-"]');
      if (ccfDebug && retryCount === 0) {
        console.log('[CCF] tryInit: .ccf-section=', containers.length, ' [id^=ccf-section-]=', altContainers.length);
      }
      
      if (containers.length === 0) {
        if (altContainers.length > 0) {
          altContainers.forEach(function (container) {
            // console.log('[CCF] Initializing container:', container.id);
            initForm(container);
          });
          return;
        }
        
        // Retry if we haven't exceeded max retries
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(tryInit, 200);
          return;
        }
        
        console.warn('[CCF] Could not find containers after', maxRetries, 'retries');
        // console.log('[CCF] Debug: All elements with "ccf" in class:', document.querySelectorAll('[class*="ccf"]').length);
        return;
      }
      
      containers.forEach(function (container) {
        // console.log('[CCF] Initializing container:', container.id);
        initForm(container);
      });
    }
    // Small delay to ensure DOM is fully ready
    setTimeout(tryInit, 100);

    // Handle theme editor section loads
    document.addEventListener('shopify:section:load', function (event) {
      const sectionId = event.detail?.sectionId;
      // console.log('[CCF] Section loaded event:', sectionId);
      if (sectionId) {
        setTimeout(function () {
          const container = document.querySelector('#ccf-section-' + sectionId) || document.querySelector('.ccf-section');
          if (container) {
            // console.log('[CCF] Initializing from section:load event');
            initForm(container);
          }
        }, 100);
      }
    });
    
    // Also watch for DOM changes in case the section is added dynamically
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(function (mutations) {
        const containers = document.querySelectorAll('.ccf-section');
        if (containers.length > 0) {
          containers.forEach(function (container) {
            if (!container.hasAttribute('data-ccf-initialized')) {
              // console.log('[CCF] Found container via MutationObserver:', container.id);
              container.setAttribute('data-ccf-initialized', 'true');
              initForm(container);
            }
          });
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  function initForm(container) {
    if (!container) {
      console.error('[CCF] Container not found');
      return;
    }
    
    // Prevent double initialization
    if (container.hasAttribute('data-ccf-initialized')) {
      // console.log('[CCF] Container already initialized:', container.id);
      return;
    }
    
    container.setAttribute('data-ccf-initialized', 'true');

    if (ccfDebug) console.log('[CCF] Form initialized for', container.id || 'container');

    const form = container.querySelector('.ccf__form');
    if (!form) {
      console.error('[CCF] Form not found in container', container);
      return;
    }

    let configScript = container.querySelector('[data-ccf-config]');
    if (!configScript && container.parentElement) {
      configScript = container.parentElement.querySelector('[data-ccf-config]');
    }
    if (!configScript) {
      console.error('[CCF] Config script not found');
      return;
    }

    let config;
    try {
      config = JSON.parse(configScript.textContent);
      // console.log('[CCF] Config loaded:', config);
    } catch (e) {
      console.error('[CCF] Failed to parse config', e);
      return;
    }

    // Get section ID from data attribute or ID
    const sectionId = container.getAttribute('data-section-id') || container.id.replace('ccf-section-', '') || container.id;
    const fieldsContainer = container.querySelector('[data-ccf-fields]');
    const topicSelect = container.querySelector('[data-ccf-topic-select]');
    const submitButton = container.querySelector('[data-ccf-submit]');
    const storageKey = 'ccf-form-' + sectionId + '-' + (window.location.pathname || '/');

    // Clear storage on success
    if (container.querySelector('[data-ccf-success]')) {
      try { localStorage.removeItem(storageKey); } catch (e) {}
    }

    // console.log('[CCF] Elements found:', { sectionId, hasFieldsContainer: !!fieldsContainer, hasTopicSelect: !!topicSelect, hasSubmitButton: !!submitButton });

    if (!fieldsContainer) {
      console.error('[CCF] Fields container not found');
      return;
    }

    if (!topicSelect) {
      console.error('[CCF] Topic select not found');
      return;
    }

    // State
    const state = {
      selectedTopicId: null,
      isSubmitting: false,
      fieldValues: {} // Store values when fields are hidden
    };

    function saveFormToStorage() {
      try {
        var data = {};
        data._topic = topicSelect ? topicSelect.value : '';
        var inputs = form.querySelectorAll('input, select, textarea');
        inputs.forEach(function (el) {
          if (el.type === 'file') return;
          if (el.type === 'hidden' && el.getAttribute('data-ccf-field') === 'error_category') return;
          if (el.type === 'checkbox') {
            var group = el.closest('.ccf__group');
            var fname = group ? group.getAttribute('data-field') : null;
            var key = el.name || (fname ? '_field_' + fname : null);
            if (!key) return;
            if (!data[key]) data[key] = [];
            if (el.checked) data[key].push(el.value);
          } else if (el.name) {
            data[el.name] = el.value;
          }
        });
        localStorage.setItem(storageKey, JSON.stringify(data));
      } catch (e) {}
    }

    function restoreFormFromStorage() {
      try {
        var raw = localStorage.getItem(storageKey);
        if (!raw) return;
        var data = JSON.parse(raw);
        if (!data || !data._topic) return;

        topicSelect.value = data._topic;
        topicSelect.dispatchEvent(new Event('change', { bubbles: true }));

        setTimeout(function () {
          var groups = fieldsContainer.querySelectorAll('.ccf__group.is-visible');
          groups.forEach(function (group) {
            var fieldName = group.getAttribute('data-field');
            var inputs = group.querySelectorAll('input, select, textarea');
            inputs.forEach(function (el) {
              if (el.type === 'file') return;
              if (el.type === 'hidden' && el.getAttribute('data-ccf-field') === 'error_category') return;
              if (el.type === 'checkbox') {
                var arr = data['_field_' + fieldName] || (el.name && data[el.name]);
                if (Array.isArray(arr) && arr.indexOf(el.value) !== -1) el.checked = true;
              } else if (el.name && data[el.name] !== undefined) {
                el.value = data[el.name];
              }
            });
          });
          if (state.selectedTopicId === 'support') updateSerialNumberVisibility();
        }, 150);
      } catch (e) {}
    }

    // Get field config for topic
    function getTopicConfig(topicId) {
      return config.topics.find(function (t) {
        return t.id === topicId;
      });
    }

    // Get field config
    function getFieldConfig(topicId, fieldName) {
      const topicConfig = getTopicConfig(topicId);
      if (!topicConfig || !topicConfig.fields) return null;
      return topicConfig.fields[fieldName] || null;
    }

    // Show/hide field groups based on topic
    function updateFieldVisibility(topicId) {
      // console.log('[CCF] updateFieldVisibility called with topicId:', topicId);
      if (!topicId) {
        // console.log('[CCF] No topicId, hiding fields container');
        fieldsContainer.classList.remove('is-visible');
        return;
      }

      const topicConfig = getTopicConfig(topicId);
      if (!topicConfig) {
        console.error('[CCF] Topic config not found for:', topicId);
        return;
      }

      // console.log('[CCF] Showing fields container');
      fieldsContainer.classList.add('is-visible');

      // Get all field groups
      const groups = fieldsContainer.querySelectorAll('.ccf__group');
      // console.log('[CCF] Found', groups.length, 'field groups');
      groups.forEach(function (group) {
        const topicsAttr = group.getAttribute('data-topic');
        if (!topicsAttr) return;

        const topics = topicsAttr.split(',').map(function (t) {
          return t.trim();
        });

        const isVisible = topics.includes(topicId);
        // console.log('[CCF] Group', group.getAttribute('data-field'), 'topics:', topics, 'isVisible:', isVisible);
        const isCurrentlyVisible = group.classList.contains('is-visible');

        if (isVisible && !isCurrentlyVisible) {
          group.classList.add('is-visible');
          enableFieldsInGroup(group);
          restoreFieldValues(group, topicId);
        } else if (!isVisible && isCurrentlyVisible) {
          saveFieldValues(group);
          group.classList.remove('is-visible');
          disableFieldsInGroup(group);
        }

        // Update required attributes based on field config
        if (isVisible) {
          updateFieldRequirements(group, topicId);
        }
      });

      // Serial number: visibility depends on product (Roof Space 2/4 only)
      if (topicId === 'support') {
        updateSerialNumberVisibility();
      }

      // Update message field maxlength based on topic
      updateMessageFieldConfig(topicId);

      // Handle ambassador topic (disable submit)
      if (topicId === 'ambassador') {
        submitButton.disabled = true;
      } else {
        submitButton.disabled = false;
      }


      // Scroll to first visible field
      setTimeout(function () {
        const firstVisible = fieldsContainer.querySelector('.ccf__group.is-visible');
        if (firstVisible) {
          firstVisible.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          const firstInput = firstVisible.querySelector('input, select, textarea');
          if (firstInput && firstInput.type !== 'hidden') {
            firstInput.focus();
          }
        }
      }, 100);
    }

    // Enable fields in group
    function enableFieldsInGroup(group) {
      const fields = group.querySelectorAll('input, select, textarea');
      fields.forEach(function (field) {
        if (field.type !== 'hidden') {
          field.disabled = false;
          field.removeAttribute('aria-hidden');
        }
      });
    }

    // Disable fields in group
    function disableFieldsInGroup(group) {
      const fields = group.querySelectorAll('input, select, textarea');
      fields.forEach(function (field) {
        if (field.type !== 'hidden') {
          field.disabled = true;
          field.setAttribute('aria-hidden', 'true');
        }
      });
    }

    // Update field requirements based on config
    function updateFieldRequirements(group, topicId) {
      const fieldName = group.getAttribute('data-field');
      if (!fieldName) return;

      const fieldConfig = getFieldConfig(topicId, fieldName);
      if (!fieldConfig) return;

      let isRequired = fieldConfig.required === true;

      // Serial number: only required when product is Roof Space 2 or Roof Space 4
      if (fieldName === 'serial_number' && fieldConfig.serialNumberProducts) {
        const productSelect = container.querySelector('[data-ccf-field="product"]');
        const productValue = productSelect ? productSelect.value.trim() : '';
        const serialProducts = fieldConfig.serialNumberProducts;
        isRequired = Array.isArray(serialProducts) && serialProducts.indexOf(productValue) !== -1;
      }

      const inputs = group.querySelectorAll('input, select, textarea');
      const label = group.querySelector('.ccf__label');
      const requiredIndicator = group.querySelector('.ccf__required');
      const optionalIndicator = group.querySelector('.ccf__optional');

      inputs.forEach(function (input) {
        if (input.type === 'hidden' || input.type === 'checkbox') return;

        if (isRequired) {
          input.setAttribute('required', 'required');
          input.setAttribute('aria-required', 'true');
        } else {
          input.removeAttribute('required');
          input.removeAttribute('aria-required');
        }
      });

      // Update label indicators
      if (label) {
        if (isRequired && requiredIndicator) {
          requiredIndicator.style.display = 'inline';
        } else if (requiredIndicator) {
          requiredIndicator.style.display = 'none';
        }

        if (!isRequired && optionalIndicator) {
          optionalIndicator.style.display = 'inline';
        } else if (optionalIndicator) {
          optionalIndicator.style.display = 'none';
        }
      }

      // Handle multiselect (checkboxes)
      if (fieldConfig.type === 'multiselect') {
        const checkboxes = group.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach(function (checkbox) {
          if (isRequired) {
            checkbox.setAttribute('data-required', 'true');
          } else {
            checkbox.removeAttribute('data-required');
          }
        });
      }

      // Serial number: update label indicators
      if (fieldName === 'serial_number') {
        const reqInd = group.querySelector('[data-ccf-required-indicator]');
        const optInd = group.querySelector('[data-ccf-optional-indicator]');
        if (reqInd) reqInd.style.display = isRequired ? 'inline' : 'none';
        if (optInd) optInd.style.display = isRequired ? 'none' : 'inline';
      }
    }

    // Toggle serial number visibility/required based on product (Roof Space 2/4 only)
    function updateSerialNumberVisibility() {
      const serialGroup = fieldsContainer.querySelector('.ccf__group[data-field="serial_number"]');
      if (!serialGroup) return;

      const productSelect = container.querySelector('[data-ccf-field="product"]');
      const productValue = productSelect ? productSelect.value.trim() : '';
      const fieldConfig = getFieldConfig('support', 'serial_number');
      const serialProducts = (fieldConfig && fieldConfig.serialNumberProducts) || ['Roof Space 2', 'Roof Space 4'];
      const showSerial = Array.isArray(serialProducts) && serialProducts.indexOf(productValue) !== -1;

      if (showSerial) {
        serialGroup.classList.add('is-visible');
        serialGroup.querySelectorAll('input').forEach(function (input) {
          input.disabled = false;
          input.removeAttribute('aria-hidden');
          input.setAttribute('required', 'required');
          input.setAttribute('aria-required', 'true');
        });
        updateFieldRequirements(serialGroup, 'support');
      } else {
        serialGroup.classList.remove('is-visible');
        const serialInput = serialGroup.querySelector('input');
        if (serialInput) {
          serialInput.value = '';
          serialInput.disabled = true;
          serialInput.setAttribute('aria-hidden', 'true');
          serialInput.removeAttribute('required');
          serialInput.removeAttribute('aria-required');
        }
        const reqInd = serialGroup.querySelector('[data-ccf-required-indicator]');
        const optInd = serialGroup.querySelector('[data-ccf-optional-indicator]');
        if (reqInd) reqInd.style.display = 'none';
        if (optInd) optInd.style.display = 'inline';
        const errEl = serialGroup.querySelector('.ccf__error');
        if (errEl) clearFieldError(errEl);
      }
    }

    // Update message field config
    function updateMessageFieldConfig(topicId) {
      const messageGroup = fieldsContainer.querySelector('[data-field="message"]');
      if (!messageGroup) return;

      const topicConfig = getTopicConfig(topicId);
      if (!topicConfig || !topicConfig.fields.message) return;

      const fieldConfig = topicConfig.fields.message;
      const textarea = messageGroup.querySelector('textarea');
      const charCount = messageGroup.querySelector('[data-ccf-char-count]');
      const charCurrent = charCount?.querySelector('[data-ccf-char-current]');
      const charMax = charCount?.querySelector('[data-ccf-char-max]');
      const requiredIndicator = messageGroup.querySelector('[data-ccf-required-indicator]');
      const optionalIndicator = messageGroup.querySelector('[data-ccf-optional-indicator]');

      if (textarea && fieldConfig.maxlength) {
        textarea.setAttribute('maxlength', fieldConfig.maxlength);
        textarea.setAttribute('data-maxlength', fieldConfig.maxlength);
        if (charMax) {
          charMax.textContent = fieldConfig.maxlength;
        }
      }

      if (fieldConfig.required) {
        textarea.setAttribute('required', 'required');
        if (requiredIndicator) requiredIndicator.style.display = 'inline';
        if (optionalIndicator) optionalIndicator.style.display = 'none';
      } else {
        textarea.removeAttribute('required');
        if (requiredIndicator) requiredIndicator.style.display = 'none';
        if (optionalIndicator) optionalIndicator.style.display = 'inline';
      }
    }

    // Save field values before hiding
    function saveFieldValues(group) {
      const fieldName = group.getAttribute('data-field');
      if (!fieldName) return;

      const inputs = group.querySelectorAll('input, select, textarea');
      const values = {};

      inputs.forEach(function (input) {
        if (input.type === 'file') return;

        if (input.type === 'checkbox') {
          if (input.checked) {
            var key = input.name || fieldName;
            if (!values[key]) values[key] = [];
            values[key].push(input.value);
          }
        } else {
          if (input.name) values[input.name] = input.value;
        }
      });

      if (Object.keys(values).length > 0) {
        state.fieldValues[fieldName] = values;
      }
    }

    // Restore field values when showing
    function restoreFieldValues(group, topicId) {
      const fieldName = group.getAttribute('data-field');
      if (!fieldName || !state.fieldValues[fieldName]) return;

      const savedValues = state.fieldValues[fieldName];
      const inputs = group.querySelectorAll('input, select, textarea');

      inputs.forEach(function (input) {
        if (input.type === 'file') return;

        if (input.type === 'checkbox') {
          var savedArray = savedValues[input.name] || savedValues[fieldName];
          if (Array.isArray(savedArray) && savedArray.includes(input.value)) {
            input.checked = true;
          }
        } else if (input.name && savedValues[input.name] !== undefined) {
          input.value = savedValues[input.name];
        }
      });
    }

    // Topic selection handler (dropdown)
    topicSelect.addEventListener('change', function () {
      const topicId = this.value;
      
      if (!topicId) {
        // console.log('[CCF] No topic selected, hiding fields');
        state.selectedTopicId = null;
        fieldsContainer.classList.remove('is-visible');
        return;
      }

      // console.log('[CCF] Topic selected:', topicId);
      state.selectedTopicId = topicId;

      // Update hidden fields
      const topicConfig = getTopicConfig(topicId);
      const topicHidden = container.querySelector('[data-ccf-topic-hidden]');
      const subjectHidden = container.querySelector('[data-ccf-subject-hidden]');

      if (topicHidden) {
        topicHidden.value = topicId;
      }
      if (subjectHidden && topicConfig) {
        subjectHidden.value = topicConfig.title || topicId;
      }

      // Clear topic error
      const topicError = container.querySelector('#ccf-topic-error-' + sectionId);
      if (topicError) {
        clearFieldError(topicError);
      }

      // Update field visibility
      if (ccfDebug) console.log('[CCF] Topic selected:', topicId);
      updateFieldVisibility(topicId);
    });

    // Product change: toggle serial number visibility (Support topic only)
    const productSelect = container.querySelector('[data-ccf-field="product"]');
    if (productSelect) {
      productSelect.addEventListener('change', function () {
        if (state.selectedTopicId === 'support') {
          updateSerialNumberVisibility();
        }
      });
    }

    // Character count handlers
    const textareas = form.querySelectorAll('textarea[data-maxlength]');
    textareas.forEach(function (textarea) {
      function updateCharCount() {
        const maxlength = parseInt(textarea.getAttribute('data-maxlength'), 10);
        const current = textarea.value.length;
        const charCount = textarea.closest('.ccf__field').querySelector('[data-ccf-char-count]');
        const charCurrent = charCount?.querySelector('[data-ccf-char-current]');

        if (charCurrent) {
          charCurrent.textContent = current;
        }

        if (charCount) {
          charCount.classList.remove('is-warning', 'is-error');
          if (current > maxlength * 0.9) {
            charCount.classList.add('is-warning');
          }
          if (current >= maxlength) {
            charCount.classList.add('is-error');
          }
        }
      }

      textarea.addEventListener('input', updateCharCount);
      updateCharCount();
    });


    // Form validation
    function validateForm() {
      if (!state.selectedTopicId) {
        const topicError = container.querySelector('#ccf-topic-error-' + sectionId);
        if (topicError) {
          showFieldError(topicError, 'Bitte wähle ein Thema aus.');
        }
        showErrorSummary(['Bitte wähle ein Thema aus.']);
        topicSelect.focus();
        return false;
      }

      const topicConfig = getTopicConfig(state.selectedTopicId);
      if (!topicConfig) return false;

      const errors = [];
      const visibleGroups = fieldsContainer.querySelectorAll('.ccf__group.is-visible');

      visibleGroups.forEach(function (group) {
        const fieldName = group.getAttribute('data-field');
        if (!fieldName) return;

        const fieldConfig = getFieldConfig(state.selectedTopicId, fieldName);
        if (!fieldConfig) return;

        // Skip hidden fields
        if (fieldName === 'ambassador_info') return;

        // Validate required fields (include dynamically required e.g. serial_number when product is RS2/RS4)
        const input = group.querySelector('input, select, textarea');
        const effectivelyRequired = fieldConfig.required || (input && input.hasAttribute('required'));
        if (effectivelyRequired) {
          const fieldError = validateField(group, fieldName, fieldConfig);
          if (fieldError) {
            errors.push({
              field: fieldName,
              message: fieldError
            });
          }
        }
      });


      if (errors.length > 0) {
        showErrorSummary(errors.map(function (e) {
          return e.message;
        }));
        errors.forEach(function (error) {
          const errorElement = container.querySelector('#ccf-' + error.field + '-error-' + sectionId);
          if (errorElement) {
            showFieldError(errorElement, error.message);
          }
        });
        return false;
      }

      return true;
    }

    function validateField(group, fieldName, fieldConfig) {
      const inputs = group.querySelectorAll('input, select, textarea');

      // Handle multiselect
      if (fieldConfig.type === 'multiselect') {
        const checked = group.querySelectorAll('input[type="checkbox"]:checked');
        if (checked.length === 0) {
          return 'Bitte wähle mindestens eine Option aus.';
        }
        return null;
      }

      // Handle regular inputs
      for (let i = 0; i < inputs.length; i++) {
        const input = inputs[i];
        if (input.type === 'hidden' || input.type === 'file') continue;

        if (input.type === 'checkbox' && !input.checked && fieldConfig.required) {
          return 'Dieses Feld ist erforderlich.';
        }

        if (input.type !== 'checkbox') {
          const value = input.value.trim();

          if (fieldConfig.required && !value) {
            return 'Dieses Feld ist erforderlich.';
          }

          if (value && fieldConfig.maxlength && value.length > fieldConfig.maxlength) {
            return 'Maximale Zeichenanzahl überschritten (' + fieldConfig.maxlength + ').';
          }

          if (input.type === 'email' && value && !isValidEmail(value)) {
            return 'Bitte gib eine gültige E-Mail-Adresse ein.';
          }

          if ((fieldConfig.type === 'url' || input.type === 'url') && value && !isValidUrl(value)) {
            return 'Bitte gib einen gültigen Link ein (z.B. https://...).';
          }
        }
      }

      return null;
    }

    function isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    function isValidUrl(url) {
      if (!url || typeof url !== 'string') return false;
      const trimmed = url.trim();
      return /^https?:\/\/.+/.test(trimmed);
    }

    function showFieldError(errorElement, message) {
      if (!errorElement) return;
      errorElement.textContent = message;
      errorElement.classList.add('is-visible');
      errorElement.setAttribute('role', 'alert');
    }

    function clearFieldError(errorElement) {
      if (!errorElement) return;
      errorElement.textContent = '';
      errorElement.classList.remove('is-visible');
    }

    function showErrorSummary(messages) {
      let errorSummary = container.querySelector('.ccf__error-summary');
      if (!errorSummary) {
        errorSummary = document.createElement('div');
        errorSummary.className = 'ccf__error-summary';
        errorSummary.setAttribute('role', 'alert');
        errorSummary.setAttribute('id', 'ccf-error-summary-' + sectionId);
        const fieldsContainer = container.querySelector('[data-ccf-fields]');
        if (fieldsContainer) {
          fieldsContainer.insertBefore(errorSummary, fieldsContainer.firstChild);
        }
      }

      errorSummary.innerHTML = '<h3>Bitte korrigiere die folgenden Fehler:</h3><ul>' +
        messages.map(function (msg) {
          return '<li>' + escapeHtml(msg) + '</li>';
        }).join('') +
        '</ul>';

      errorSummary.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }


    // Form submission
    form.addEventListener('submit', function (e) {
      e.preventDefault();

      if (state.isSubmitting) return;

      // Clear previous errors
      container.querySelectorAll('.ccf__error').forEach(function (el) {
        clearFieldError(el);
      });
      const errorSummary = container.querySelector('.ccf__error-summary');
      if (errorSummary) {
        errorSummary.remove();
      }

      // Validate
      if (!validateForm()) {
        return;
      }

      state.isSubmitting = true;
      submitButton.disabled = true;
      submitButton.textContent = 'Wird gesendet...';

      submitForm();
    });


    function submitForm() {
      // Populate error_category hidden field (Shopify rejects array format contact[field][])
      var errorCatGroup = fieldsContainer.querySelector('.ccf__group[data-field="error_category"]');
      if (errorCatGroup) {
        var hiddenInput = errorCatGroup.querySelector('input[type="hidden"][data-ccf-field="error_category"]');
        var checkboxes = errorCatGroup.querySelectorAll('input[type="checkbox"]:checked');
        if (hiddenInput) {
          var vals = [];
          checkboxes.forEach(function (cb) { vals.push(cb.value); });
          hiddenInput.value = vals.join(', ');
        }
      }

      saveFormToStorage();

      // Re-enable submit button
      state.isSubmitting = false;
      submitButton.disabled = false;

      // Submit the form
      form.submit();
    }

    // Clear errors on input
    form.addEventListener('input', function (e) {
      const field = e.target.closest('.ccf__field');
      if (field) {
        const errorElement = field.querySelector('.ccf__error');
        if (errorElement) {
          clearFieldError(errorElement);
        }
      }
    });

    form.addEventListener('change', function (e) {
      const field = e.target.closest('.ccf__field');
      if (field) {
        const errorElement = field.querySelector('.ccf__error');
        if (errorElement) {
          clearFieldError(errorElement);
        }
      }
    });

    if (container.querySelector('[data-ccf-has-errors]')) {
      restoreFormFromStorage();
    }
  }
})();

